import "../../styles/leaveManagement.css";
import { useState, useMemo } from "react";

import LeaveTableSearch from "./components/LeaveTableSearch";
import LeavePagination from "./components/LeavePagination";
import LeaveTableHeader from "./components/LeaveTableHeader";
import LeaveTableRow from "./components/LeaveTableRow";
import LeaveTableModals from "./components/LeaveTableModals";
import { getFilteredLeaveData } from "./utils/leaveTableFilter.js";

const LeaveManagementTable = ({ attendanceData, selectedCompany }) => {
  const userData = JSON.parse(localStorage.getItem("userData"));
  const loggedEmployeeId = userData?.user?.[0]?.employee_id;
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showEmployeeLeaveDetails, setShowEmployeeLeaveDetails] =
    useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
  const [showUpdateLeaveDetails, setShowUpdateLeaveDetails] = useState(null);
  const [showApproveDeclinePage, setShowApproveDeclinePage] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Pagination and Search states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState("");

  // Filter and search logic with sorting applied
  const filteredData = useMemo(
    () =>
      getFilteredLeaveData({
        attendanceData,
        searchQuery,
        loggedEmployeeId,
        sortConfig,
      }),
    [attendanceData, searchQuery, loggedEmployeeId, sortConfig],
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Reset to first page when search query changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getSelectedEmployee = (leaveId) => {
    return attendanceData.find((employee) => employee.leave_id === leaveId);
  };

  const handleLeaveMoreDetails = (leaveId) => {
    const selectedEmployee = getSelectedEmployee(leaveId);

    if (selectedEmployee) {
      setSelectedEmployeeData(selectedEmployee);
      setShowEmployeeLeaveDetails(true);
      setActiveMenuId(false);
    }
  };

  const handleUpdateLeaveDetails = (leaveId) => {
    const selectedEmployee = getSelectedEmployee(leaveId);

    if (selectedEmployee) {
      setSelectedEmployeeData(selectedEmployee);
      setShowUpdateLeaveDetails(true);
      setActiveMenuId(false);
    }
  };

  const handleLeaveAction = (leaveId) => {
    const selectedEmployee = getSelectedEmployee(leaveId);

    if (selectedEmployee) {
      setSelectedEmployeeData(selectedEmployee);
      setShowApproveDeclinePage(true);
    }
  };

  const handleToggleMenu = (leaveId, e) => {
    if (activeMenuId === leaveId) {
      setActiveMenuId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const screenWidth = window.innerWidth;

      setActiveMenuId(leaveId);
      const menuWidth = 100;
      const menuLeft = rect.left + window.scrollX;
      const menuRight = menuLeft + menuWidth;

      if (menuRight > screenWidth) {
        setMenuPosition({
          top: rect.bottom + window.scrollY - 15,
          right: screenWidth - menuLeft - 20,
        });
      } else {
        setMenuPosition({
          top: rect.bottom + window.scrollY - 15,
          left: menuLeft - 20,
        });
      }
    }
  };

  const handleSort = (columnKey) => {
    let direction = "asc";

    // If clicking the same column, toggle direction
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key: columnKey, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  const goToPreviousPage = () => {
    setCurrentPage((prevPage) => prevPage - 1);
  };

  const goToNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  return (
    <div>
      {/* Search Bar */}
      <LeaveTableSearch
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearchChange={handleSearchChange}
      />

      <table className="leave_table">
        <LeaveTableHeader sortConfig={sortConfig} handleSort={handleSort} />
        <tbody>
          {currentData.length === 0 ? (
            <tr>
              <td
                colSpan={10}
                className="text-center py-8 text-sm text-gray-400"
              >
                {searchQuery
                  ? "No results found for your search."
                  : "No leave requests."}
              </td>
            </tr>
          ) : (
            currentData.map((employee) => (
              <LeaveTableRow
                key={employee.leave_id}
                employee={employee}
                loggedEmployeeId={loggedEmployeeId}
                activeMenuId={activeMenuId}
                menuPosition={menuPosition}
                handleToggleMenu={handleToggleMenu}
                handleLeaveMoreDetails={handleLeaveMoreDetails}
                handleUpdateLeaveDetails={handleUpdateLeaveDetails}
                handleLeaveApprove={handleLeaveAction}
                handleLeaveDecline={handleLeaveAction}
              />
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <LeavePagination
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        goToPreviousPage={goToPreviousPage}
        goToNextPage={goToNextPage}
        searchQuery={searchQuery}
        filteredData={filteredData}
      />

      <LeaveTableModals
        showEmployeeLeaveDetails={showEmployeeLeaveDetails}
        showUpdateLeaveDetails={showUpdateLeaveDetails}
        showApproveDeclinePage={showApproveDeclinePage}
        selectedEmployeeData={selectedEmployeeData}
        selectedCompany={selectedCompany}
        setShowEmployeeLeaveDetails={setShowEmployeeLeaveDetails}
        setShowUpdateLeaveDetails={setShowUpdateLeaveDetails}
        setSelectedEmployeeData={setSelectedEmployeeData}
        setShowApproveDeclinePage={setShowApproveDeclinePage}
      />
    </div>
  );
};

export default LeaveManagementTable;
